import React from "react";

import { useMountEffect } from "../../hooks/useMountEffect";
import { FormatOptions, IPhoneNumberField } from "../../models/formFields";
import { FormInputFieldWrapper } from "../FormInputFieldWrapper";
import { ensureCleaveLoaded, getFormatter } from "../formatting";
import { useFormField } from "../hooks/useFormField";

const COUNTRY_PREFIX = "+1";
const COUNTRY_DELIMITERS = [" ", "(", ")", " ", "-"];
const COUNTRY_BLOCKS = [COUNTRY_PREFIX.length, 0, 3, 0, 3, 4];

const getFormatOptions = (): FormatOptions => {
    return {
        type: "general",
        prefix: COUNTRY_PREFIX,
        delimiters: COUNTRY_DELIMITERS,
        blocks: COUNTRY_BLOCKS,
        numericOnly: true,
    };
};

export const FormPhoneNumber = <T extends string>(
    props: IPhoneNumberField<T>,
): React.ReactNode => {
    const { type = "phone" } = props;

    const field = useFormField<T, HTMLInputElement>({
        ...props,
        type: type,
    });

    // Load cleave-zen on mount, then register cursor tracker.
    // Dynamic load avoids ESM/CJS cycle during SSR module evaluation.
    // State toggle triggers re-render so inputProps.value picks up the
    // real formatter (instead of the passthrough used before load).
    const [, setFormattingReady] = React.useState(false);
    useMountEffect(() => {
        ensureCleaveLoaded().then(() => {
            setFormattingReady(true);
            if (field.inputRef.current) {
                const formatter = getFormatter(getFormatOptions());
                formatter.registerCursorTracker(field.inputRef.current);
            }
        });
    });

    const onKeyDown = (_: React.KeyboardEvent<HTMLInputElement>): void => {
        // Modify text selection to exclude prefix
        if (field.inputRef.current) {
            if (!field.inputRef.current.selectionStart) {
                field.inputRef.current.selectionStart = COUNTRY_PREFIX.length;
            }
            if (field.inputRef.current.selectionStart < COUNTRY_PREFIX.length) {
                field.inputRef.current.selectionStart = COUNTRY_PREFIX.length;
            }
        }
    };

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        // Format value
        const formatter = getFormatter(getFormatOptions());
        const original = event.currentTarget.value;
        const formatted = formatter.format(original);
        if (original !== formatted) {
            event.currentTarget.value = formatted;
        }
    };

    const baseInputProps = field.inputProps;
    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
        ...baseInputProps,
        type: "tel",
        inputMode: "tel" as const,
        autoComplete: baseInputProps.autoComplete ?? "tel",
        value: (() => {
            if (
                baseInputProps.value !== undefined &&
                baseInputProps.value !== ""
            ) {
                const formatter = getFormatter(getFormatOptions());
                let rawValue = String(baseInputProps.value).replace(
                    /[^\d]/g,
                    "",
                );
                // add US prefix if it is missing
                rawValue = rawValue.replace(
                    /^(1)?(.*)?$/,
                    `${COUNTRY_PREFIX}$2`,
                );
                return formatter.format(rawValue);
            }
            return baseInputProps.value;
        })(),
        onChange: (event: React.FormEvent<HTMLInputElement>) => {
            field.handleChange(event, handleChange);
        },
        onKeyDown,
    };

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <input ref={field.inputRef} {...inputProps} />
        </FormInputFieldWrapper>
    );
};
